import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../dialogLayout';
import { dangerButtonSx, secondaryButtonSx } from '../../core/pvtaStyles';
import EventRegistrationBuilder from './EventRegistrationBuilder';
import EventRegistrationsDialog from './EventRegistrationsDialog';

interface EventMoreOptionsDialogProps {
    open: boolean;
    eventId: number;
    eventTitle: string;
    onClose: () => void;
}

/**
 * Sammelpunkt fuer alles, was den Standard-Eventdialog nicht aufblaehen soll
 * (Spec §11/§22): Struktur der Zusatzanmeldung sowie der Einstieg in
 * Anmeldeuebersicht und Export.
 */
const EventMoreOptionsDialog: React.FC<EventMoreOptionsDialogProps> = ({
    open,
    eventId,
    eventTitle,
    onClose,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [registrationsOpen, setRegistrationsOpen] = useState(false);
    const [isBuilderDirty, setIsBuilderDirty] = useState(false);
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

    // MUI unmountet den Dialog-Inhalt beim Schliessen -- ein ungespeicherter Entwurf im
    // Builder waere sonst kommentarlos weg (Esc, Backdrop-Klick oder "Schließen"). Daher
    // erst nachfragen, wenn der Builder ueber `onDirtyChange` ungespeicherte Aenderungen
    // meldet.
    const handleClose = (): void => {
        if (isBuilderDirty) {
            setDiscardConfirmOpen(true);
            return;
        }
        onClose();
    };

    const handleDiscardConfirmed = (): void => {
        setDiscardConfirmOpen(false);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                fullScreen={isMobile}
                maxWidth={EVENT_DIALOG_MAX_WIDTH}
                PaperProps={EVENT_DIALOG_PAPER_SX}
                aria-label="Weitere Optionen"
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Weitere Optionen</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Zusätzliche Angaben, die Teilnehmer bei ihrer Zusage machen müssen.
                        </Typography>

                        <EventRegistrationBuilder eventId={eventId} onDirtyChange={setIsBuilderDirty} />

                        <Divider />

                        <Button
                            startIcon={<GroupsRoundedIcon />}
                            onClick={() => setRegistrationsOpen(true)}
                            sx={{ ...secondaryButtonSx }}
                        >
                            Anmeldungen &amp; Export
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} sx={{ ...secondaryButtonSx }}>
                        Schließen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
                <DialogTitle>Ungespeicherte Änderungen verwerfen?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Die Zusatzanmeldung wurde noch nicht gespeichert. Wenn du jetzt
                        schließt, gehen deine Änderungen verloren.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscardConfirmOpen(false)} sx={{ ...secondaryButtonSx }}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleDiscardConfirmed} sx={{ ...dangerButtonSx }}>
                        Verwerfen
                    </Button>
                </DialogActions>
            </Dialog>

            <EventRegistrationsDialog
                open={registrationsOpen}
                eventId={eventId}
                eventTitle={eventTitle}
                onClose={() => setRegistrationsOpen(false)}
            />
        </>
    );
};

export default EventMoreOptionsDialog;
